import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppShell } from "@/components/layout/AppShell";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import {
  CHART_COLORS,
  ChartCard,
  ChartState,
  formatMoneyCLP,
  formatNumber,
  tooltipStyle,
} from "@/components/bi/BiPrimitives";
import { tipoMPCorto, tipoMPLabel } from "@/lib/mercado-publico-catalog";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/categorias")({
  head: () => ({ meta: [{ title: "Reporte Categorías — LicitaPro" }] }),
  component: Page,
});

function Page() {
  const { filters } = useBiFilters();
  const categorias = useQuery({
    queryKey: ["bi", "rankings", "categoria", filters],
    queryFn: () => biAnalyticsService.rankings(filters, "categoria", "count", 15),
  });
  const tipos = useQuery({
    queryKey: ["bi", "breakdown", "tipoMP", filters],
    queryFn: () => biAnalyticsService.breakdown(filters, "tipoMP"),
  });
  const monedas = useQuery({
    queryKey: ["bi", "breakdown", "moneda", filters],
    queryFn: () => biAnalyticsService.breakdown(filters, "moneda"),
  });
  const tasaExito = useQuery({
    queryKey: ["bi", "tasa-exito-rubro", filters],
    queryFn: () => biAnalyticsService.tasaExitoRubro(filters),
  });

  return (
    <AppShell
      title="Categorías y canales"
      subtitle="Qué se compra: rubros UNSPSC, tipos de licitación y monedas"
      actions={
        <Link
          to="/bi"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="size-3.5" /> Reportes
        </Link>
      }
    >
      <BiFilterBar />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <ChartCard
          title="Top categorías UNSPSC"
          subtitle="Volumen de licitaciones por rubro"
          className="xl:col-span-2"
        >
          <ChartState
            loading={categorias.isLoading}
            error={categorias.error}
            empty={!categorias.data?.length}
            height={400}
          >
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={categorias.data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  width={180}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Licitaciones" fill="var(--color-chart-1)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>

        <ChartCard title="Monedas" subtitle="Monto informado por moneda">
          <ChartState
            loading={monedas.isLoading}
            error={monedas.error}
            empty={!monedas.data?.length}
            height={400}
          >
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={monedas.data}
                  dataKey="count"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={2}
                >
                  {(monedas.data ?? []).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {(monedas.data ?? []).map((row) => (
                <div key={row.name} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{row.name}</span>
                  <span className="text-foreground tabular-nums">
                    {formatNumber(row.count)}
                    {row.name === "CLP" ? ` · ${formatMoneyCLP(row.amount)}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </ChartState>
        </ChartCard>
      </div>

      <ChartCard
        title="Tasa de éxito por rubro"
        subtitle="De lo postulado y ya resuelto (adjudicado o perdido), qué proporción se ganó"
        className="mb-4"
      >
        <ChartState
          loading={tasaExito.isLoading}
          error={tasaExito.error}
          empty={!tasaExito.data?.length}
          height={200}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Rubro</th>
                  <th className="text-right font-medium py-2">Postuladas</th>
                  <th className="text-right font-medium py-2">Ganadas</th>
                  <th className="text-right font-medium py-2">Tasa de éxito</th>
                </tr>
              </thead>
              <tbody>
                {(tasaExito.data ?? []).map((row) => (
                  <tr key={row.rubro} className="border-b border-border/60 last:border-0">
                    <td className="py-2 text-foreground">{row.rubro}</td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {formatNumber(row.postuladas)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {formatNumber(row.ganadas)}
                    </td>
                    <td className="py-2 text-right tabular-nums font-medium text-foreground">
                      {row.tasaExito}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartState>
      </ChartCard>

      <ChartCard title="Tipos de licitación" subtitle="Códigos oficiales de Mercado Público">
        <ChartState loading={tipos.isLoading} error={tipos.error} empty={!tipos.data?.length} height={280}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={(tipos.data ?? []).map((r) => ({ ...r, corto: tipoMPCorto(r.name) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="corto"
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(label, payload) =>
                  tipoMPLabel(payload?.[0]?.payload?.name ?? String(label))
                }
              />
              <Bar dataKey="count" name="Licitaciones" fill="var(--color-chart-2)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartState>
      </ChartCard>
    </AppShell>
  );
}
